import { css, cx } from '@lastbrand/web-ui-styled-system/css'
import { forwardRef } from 'react'

import { SectionContentWrapper } from '@src/ds/blocks/section-content-wrapper'

import type { SectionContentContainerProps } from './section-content-container.types'

/**
 * Inner content-frame max-width, in px. Mirrors the Figma DS grid inner-content
 * width (1504 = 94rem). There is no DS size token for this width — the grid
 * exposes only `breakpoint-lg` (1200px) — so it stays a local layout constant,
 * matching the raw `94rem` used elsewhere (e.g. `influencer-v2-ds`). Combined
 * with the `space-48` gutter on the outer band, content sits edge-aligned until
 * the viewport exceeds 1504 + 2×48 = 1600px — the same frame the PL widgets
 * express as a single `maxWidth: '1600px'` box.
 */
// eslint-disable-next-line i18next/no-literal-string -- layout max-width constant, not user-facing copy
const MAX_CONTENT_WIDTH = '1504px'

// The DS responsive horizontal gutters. Width, box-sizing and the background cap
// come from `SectionContentWrapper`, which renders the band.
const outerBandClass = css({
  paddingInline: { base: 'space-16', lg: 'space-48' },
})

// Inner content frame: centered, capped at the DS content width.
const contentFrameClass = css({
  width: 'full',
  maxWidth: MAX_CONTENT_WIDTH,
  marginInline: 'auto',
})

/**
 * Shared layout wrapper for DS Homepage sections and DS PL widgets.
 *
 * Renders the canonical DS content frame that every section/widget currently
 * re-declares by hand: a `SectionContentWrapper` band carrying the responsive
 * horizontal gutters (`space-16` / `space-48`), plus an inner frame centered at
 * `1504px`.
 *
 * The band comes from `SectionContentWrapper`, so it is capped at `bp-desktop-cap`
 * like every other DS section background (STRFRNT-16552 Part 1). That never moves
 * content — the inner 1504px frame stays centered in the viewport whether the band
 * is capped or full width — so at every width content renders exactly where it does
 * today; only the background stops expanding past the cap. Header, footer, and
 * full-bleed-by-design bands pass `fullBleed` through to the wrapper.
 *
 * Section-specific concerns stay with the caller:
 *  - background color / band styling → `className` (applied to the band so the
 *    background spans the band's full width)
 *  - vertical spacing (paddingBlock / margins) → `className`
 *  - content-frame-only overrides → `contentClassName`
 *  - opting out of the background cap → `fullBleed`
 *  - semantic landmark → `as` (e.g. `as="section"`); defaults to `div`
 *
 * Any other standard HTML attribute (`id`, `style`, `role`, `aria-*`, event
 * handlers, …) is forwarded to the band.
 *
 * The forwarded `ref` lands on the band, so impression / visibility observers
 * measure the whole section band (matching the legacy single-`div` wrappers).
 */
// `HTMLElement` (not `HTMLDivElement`) because `as` can render a non-div band
// (e.g. `as="section"`); the ref resolves to the rendered element's type.
export const SectionContentContainer = forwardRef<HTMLElement, SectionContentContainerProps>(
  function SectionContentContainer({ children, className, contentClassName, ...rest }, ref) {
    return (
      <SectionContentWrapper ref={ref} className={cx(outerBandClass, className)} {...rest}>
        <div className={cx(contentFrameClass, contentClassName)}>{children}</div>
      </SectionContentWrapper>
    )
  },
)
